import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import type { ActivityAPI } from '../../../types';
import type { Activity } from '../types';

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Formats activity due date and time for display
 * Due times are stored in PST timezone and converted to browser's local timezone
 * @param due_date - ISO date string (YYYY-MM-DD)
 * @param due_time - Time string (HH:MM:SS) in PST timezone
 * @param completed - Whether the activity is completed
 * @returns Formatted date/time string
 */
export const formatActivityDateTime = (due_date: string, due_time: string, completed: boolean): string => {
  try {
    // Parse as PST time and convert to local browser timezone
    const pstDateTime = dayjs.tz(`${due_date} ${due_time}`, 'America/Los_Angeles');
    const localDateTime = pstDateTime.local();

    const now = dayjs();
    const today = now.startOf('day');
    const activityDate = localDateTime.startOf('day');

    // Format time as "h:mm AM/PM"
    const timeStr = localDateTime.format('h:mm A');

    if (completed) {
      // Show just the date
      return localDateTime.format('MMM D, YYYY');
    }

    // Check if overdue (activity date is before today)
    if (activityDate.isBefore(today)) {
      return localDateTime.format('MMM D, YYYY');
    }

    // Check if today
    if (activityDate.isSame(today)) {
      return `Today ${timeStr}`;
    }

    // Check if tomorrow
    const tomorrow = today.add(1, 'day');
    if (activityDate.isSame(tomorrow)) {
      return `Tomorrow ${timeStr}`;
    }

    // Otherwise show full date
    return `${localDateTime.format('MMM D, YYYY')}, ${timeStr}`;
  } catch {
    // Fallback if date parsing fails
    return due_date;
  }
};

/**
 * Checks if an activity is due today (in browser's local timezone)
 */
export const isActivityToday = (activity: Activity): boolean => {
  try {
    const pstDateTime = dayjs.tz(`${activity.due_date} ${activity.due_time}`, 'America/Los_Angeles');
    const localDateTime = pstDateTime.local();
    const today = dayjs().startOf('day');
    return localDateTime.startOf('day').isSame(today);
  } catch {
    return false;
  }
};

/**
 * Checks if an activity is overdue (due date is before today in browser's local timezone)
 */
export const isActivityOverdue = (activity: Activity): boolean => {
  if (activity.completed) {
    return false;
  }
  try {
    const pstDateTime = dayjs.tz(`${activity.due_date} ${activity.due_time}`, 'America/Los_Angeles');
    const localDateTime = pstDateTime.local();
    const today = dayjs().startOf('day');
    return localDateTime.startOf('day').isBefore(today);
  } catch {
    return false;
  }
};

/**
 * Checks if an activity is due tomorrow (in browser's local timezone)
 */
export const isActivityTomorrow = (activity: Activity): boolean => {
  try {
    const pstDateTime = dayjs.tz(`${activity.due_date} ${activity.due_time}`, 'America/Los_Angeles');
    const localDateTime = pstDateTime.local();
    const tomorrow = dayjs().add(1, 'day').startOf('day');
    return localDateTime.startOf('day').isSame(tomorrow);
  } catch {
    return false;
  }
};

/**
 * Checks if an activity is due this week (from Monday to Sunday of current week in browser's local timezone)
 */
export const isActivityThisWeek = (activity: Activity): boolean => {
  try {
    // Convert PST time to local time
    const pstDateTime = dayjs.tz(`${activity.due_date} ${activity.due_time}`, 'America/Los_Angeles');
    const localDateTime = pstDateTime.local();
    const activityDate = localDateTime.startOf('day');

    // Calculate current week in local timezone
    const now = dayjs();
    const today = now.startOf('day');
    const dayOfWeek = now.day();
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

    const startOfWeek = today.subtract(daysFromMonday, 'day');
    const endOfWeek = startOfWeek.add(6, 'day');

    return (activityDate.isSame(startOfWeek) || activityDate.isAfter(startOfWeek)) &&
           (activityDate.isSame(endOfWeek) || activityDate.isBefore(endOfWeek));
  } catch {
    return false;
  }
};

/**
 * Checks if an activity is due next week (Monday to Sunday of next week in browser's local timezone)
 */
export const isActivityNextWeek = (activity: Activity): boolean => {
  try {
    // Convert PST time to local time
    const pstDateTime = dayjs.tz(`${activity.due_date} ${activity.due_time}`, 'America/Los_Angeles');
    const localDateTime = pstDateTime.local();
    const activityDate = localDateTime.startOf('day');

    // Calculate next week in local timezone
    const now = dayjs();
    const today = now.startOf('day');
    const dayOfWeek = now.day();
    const daysUntilNextMonday = ((7 - dayOfWeek) % 7) + 1;

    const startOfNextWeek = today.add(daysUntilNextMonday, 'day');
    const endOfNextWeek = startOfNextWeek.add(6, 'day');

    return (activityDate.isSame(startOfNextWeek) || activityDate.isAfter(startOfNextWeek)) &&
           (activityDate.isSame(endOfNextWeek) || activityDate.isBefore(endOfNextWeek));
  } catch {
    return false;
  }
};

/**
 * Converts API activity response to UI activity format
 * @param apiActivity - Activity from API
 * @returns UI activity format
 */
export const convertAPIActivityToActivity = (apiActivity: ActivityAPI): Activity => {
  return {
    id: apiActivity.id,
    title: apiActivity.subject,
    due_date: apiActivity.due_date,
    due_time: apiActivity.due_time || '00:00:00',
    completed: Boolean(apiActivity.done_at),
    isImportant: apiActivity.is_important,
    user: apiActivity.user ?? undefined,
    user_name: apiActivity.user_name,
    customer: apiActivity.customer,
    customer_name: apiActivity.customer_name,
    note: apiActivity.note,
  };
};
